import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Button } from "react-bootstrap";

import { Guest } from "../../api";
import api from "../../components/api";
import DraggableTableIcon, {
  TableType,
} from "../../components/DroppableTableIcon/DroppableTableIcon";
import "./WeddingTablePublishedPage.css";
import roundTable from "../../icons/round_table.svg";
import circle from "../../icons/circle.svg";

const GRID_SIZE = 20; // 网格在屏幕上的像素大小
const REAL_GRID_SIZE = 50; // 每个网格代表的实际厘米数

interface GuestWithTablemates extends Guest {
  tablemates?: Guest[];
}

interface ImageType {
  id: string;
  url: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

function WeddingTablePublishedPage() {
  const navigate = useNavigate();
  const { weddingTableId } = useParams<{ weddingTableId: string }>();

  const [guests, setGuests] = useState<Guest[]>([]);
  const [tables, setTables] = useState<TableType[]>([]);
  const [layoutWidth, setLayoutWidth] = useState<number>(600);
  const [layoutHeight, setLayoutHeight] = useState<number>(600);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredGuests, setFilteredGuests] = useState<Guest[]>([]);
  const [guestListVisible, setGuestListVisible] = useState<boolean>(false);
  const [selectedGuestTable, setSelectedGuestTable] = useState<string | null>(
    null,
  );
  const [selectedTableGuests, setSelectedTableGuests] = useState<Guest[]>([]);
  const [showTableGuestList, setShowTableGuestList] = useState<boolean>(false);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [listPosition, setListPosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [scalePosition, setScalePosition] = useState({
    x: window.innerWidth - 100,
    y: window.innerHeight - 100,
  });
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const [images, setImages] = useState<ImageType[]>([]);
  
  const [coupleNames, setCoupleNames] = useState<string>("");
  const [coupleNamesFont, setCoupleNamesFont] = useState<string>("Lora");
  const [coupleNamesFontSize, setCoupleNamesFontSize] = useState<number>(16);
  const [coupleNamesPosition, setCoupleNamesPosition] = useState<{ x: number, y: number }>({ x: 0, y: 20 });
  
  const [weddingDate, setWeddingDate] = useState<string>("");
  const [weddingDateFont, setWeddingDateFont] = useState<string>("Lora");
  const [weddingDateFontSize, setWeddingDateFontSize] = useState<number>(14);
  const [weddingDatePosition, setWeddingDatePosition] = useState<{ x: number, y: number }>({ x: 0, y: 50 });
  
  const [noResultsMessage, setNoResultsMessage] = useState<string>("");

  const handleGuestItemClick = (guest: Guest) => {
    if (guest.guest_name && guest.table) {
      const tableId = guest.table.split("/").pop() || null;
      setSearchTerm(guest.guest_name);
      setSelectedGuestTable(tableId);
      setGuestListVisible(false);

      setTimeout(() => {
        const tableElement = document.querySelector(
          `.draggable-table[data-id="${tableId}"]`
        );
        
        if (tableElement) {
          const container = document.querySelector('.tables-container-wrapper');
          
          if (container) {
            const tableRect = tableElement.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            
            const relativeTop = tableRect.top - containerRect.top + container.scrollTop;
            const relativeLeft = tableRect.left - containerRect.left + container.scrollLeft;
            
            container.scrollTo({
              top: relativeTop - containerRect.height / 2 + tableRect.height / 2,
              left: relativeLeft - containerRect.width / 2 + tableRect.width / 2,
              behavior: 'smooth'
            });
          }
        } else {
          console.warn(`未找到ID为${tableId}的桌子元素`);
        }
      }, 300);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target) return;
    if (
      !target.closest(".search-bar") &&
      !target.classList.contains("guest-item")
    ) {
      setGuestListVisible(false);
    }
  };
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const applyTableStyles = useCallback(() => {
    setTimeout(() => {
      tables.forEach(table => {
        const tableElement = document.querySelector(`.table-content.draggable-table[data-id="${table.id}"]`);
        if (tableElement) {
          const rotation = table.rotation || 0;
          const flipX = table.flipX ? -1 : 1;
          const flipY = table.flipY ? -1 : 1;
          
          const transform = `rotate(${rotation}deg) scaleX(${flipX}) scaleY(${flipY})`;
          (tableElement as HTMLElement).style.transform = transform;
          
          const nameElement = tableElement.querySelector('div[style*="transform: translate(-50%, -50%)"]');
          
          if (nameElement) {
            (nameElement as HTMLElement).style.position = 'absolute';
            (nameElement as HTMLElement).style.top = '50%';
            (nameElement as HTMLElement).style.left = '50%';
            (nameElement as HTMLElement).style.transform = `translate(-50%, -50%) scaleX(${flipX}) scaleY(${flipY})`;
            (nameElement as HTMLElement).style.textAlign = 'center';
          } else {
            const divElements = tableElement.querySelectorAll('div');
            divElements.forEach(div => {
              const style = window.getComputedStyle(div);
              if (style.transform && style.transform.includes('translate')) {
                div.style.transform = `${style.transform} scaleX(${flipX}) scaleY(${flipY})`;
              }
            });
          }
          
          console.log(`发布页初始化应用桌子样式 ${table.id}: rotation=${rotation}, flipX=${flipX}, flipY=${flipY}`);
        }
      });
    }, 100);
  }, [tables]);

  const loadTableStyle = useCallback(() => {
    if (weddingTableId) {
      // 获取婚礼场地尺寸
      api.weddings.getWeddingWeddingTableWeddingTableIdGet(weddingTableId).then((data) => {
        if (data.data.wedding_style) {
          try {
            const wedding_style = JSON.parse(data.data.wedding_style);
            const pixelsPerCm = GRID_SIZE / REAL_GRID_SIZE;
            
            // 设置场地尺寸
            setLayoutWidth(wedding_style.width || 1550);
            setLayoutHeight(wedding_style.height || 1550);
            
            // 加载图片信息
            if (wedding_style.images) {
              setImages(wedding_style.images.map((img: any) => ({
                ...img,
                x: img.x * pixelsPerCm,
                y: img.y * pixelsPerCm,
                width: img.width * pixelsPerCm,
                height: img.height * pixelsPerCm
              })));
            }
            
            // 加载新人姓名和字体设置
            if (wedding_style.coupleNames) {
              setCoupleNames(wedding_style.coupleNames);
            }
            if (wedding_style.coupleNamesFont) {
              setCoupleNamesFont(wedding_style.coupleNamesFont);
            }
            if (wedding_style.coupleNamesFontSize) {
              setCoupleNamesFontSize(wedding_style.coupleNamesFontSize);
            }
            // 加载新人姓名位置
            if (wedding_style.coupleNamesPosition) {
              setCoupleNamesPosition({
                x: wedding_style.coupleNamesPosition.x * pixelsPerCm,
                y: wedding_style.coupleNamesPosition.y * pixelsPerCm
              });
            } else {
              // 默认位置设在顶部中间
              setCoupleNamesPosition({ 
                x: (wedding_style.width || 1550) * pixelsPerCm / 2 - 100, 
                y: 20 
              });
            }
            // 加载婚礼日期和字体设置
            if (wedding_style.weddingDate) {
              setWeddingDate(wedding_style.weddingDate);
            }
            if (wedding_style.weddingDateFont) {
              setWeddingDateFont(wedding_style.weddingDateFont);
            }
            if (wedding_style.weddingDateFontSize) {
              setWeddingDateFontSize(wedding_style.weddingDateFontSize);
            }
            // 加载婚礼日期位置
            if (wedding_style.weddingDatePosition) {
              setWeddingDatePosition({
                x: wedding_style.weddingDatePosition.x * pixelsPerCm,
                y: wedding_style.weddingDatePosition.y * pixelsPerCm
              });
            } else {
              // 默认位置设在新人姓名下方
              setWeddingDatePosition({ 
                x: (wedding_style.width || 1550) * pixelsPerCm / 2 - 100, 
                y: 60 
              });
            }
          } catch (e) {
            console.warn("Invalid wedding_style JSON:", e);
          }
        }
      });

      // 获取桌子布局
      api.tables
        .getWeddingsWeddingTableIdTablesWeddingTableWeddingTableIdTablesGet(weddingTableId)
        .then((data) => {
          const tableTypes = data.data.contents.map((table) => {
            if (table.table_style) {
              const style = JSON.parse(table.table_style);
              const pixelsPerCm = GRID_SIZE / REAL_GRID_SIZE;
              return {
                ...style,
                // 只转换单位，不应用缩放
                x: style.x * pixelsPerCm,
                y: style.y * pixelsPerCm,
                width: style.width * pixelsPerCm,
                height: style.height * pixelsPerCm,
                rotation: style.rotation || 0,
                flipX: style.flipX || false,
                flipY: style.flipY || false
              };
            }
            return {
              id: table.table_id,
              name: table.table_name,
              x: 0,
              y: 0,
              width: 80,
              height: 80,
              rotation: 0,
              flipX: false,
              flipY: false,
              imageUrl: roundTable,
            };
          });
          setTables(tableTypes);
        });

      // 获取宾客信息
      api.guests
        .getWeddingsWeddingTableIdGuestsWeddingTableWeddingTableIdGuestsGet(weddingTableId)
        .then((data) => {
          setGuests(data.data.contents);
        });
    }
  }, [weddingTableId]);

  useEffect(() => {
    if (tables.length > 0) {
      applyTableStyles();
    }
  }, [tables, applyTableStyles]);

  useEffect(() => {
    loadTableStyle();
  }, [loadTableStyle]);

  useEffect(() => {
    const container = document.querySelector(".tables-container");
    if (container && layoutWidth && layoutHeight) {
      const containerWrapper = document.querySelector(
        ".tables-container-wrapper",
      ) as HTMLElement;
      
      if (containerWrapper) {
        const wrapperWidth = containerWrapper.clientWidth - 40;
        const wrapperHeight = containerWrapper.clientHeight - 40;

        const scaleX = wrapperWidth / layoutWidth;
        const scaleY = wrapperHeight / layoutHeight;
        const scale = Math.min(scaleX, scaleY, 1);

        (container as HTMLElement).style.width = `${layoutWidth}px`;
        (container as HTMLElement).style.height = `${layoutHeight}px`;

        setTimeout(() => {
          const scrollAmount = (containerWrapper.scrollWidth - containerWrapper.clientWidth) / 2;
          containerWrapper.scrollLeft = scrollAmount;
        }, 100);
      }
    }
  }, [layoutWidth, layoutHeight]);

  useEffect(() => {
    if (containerRef) {
      const scrollLeft = (containerRef.scrollWidth - containerRef.clientWidth) / 2;
      containerRef.scrollLeft = scrollLeft;
    }
  }, [containerRef, layoutWidth, layoutHeight]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setSearchTerm(searchValue);
    filterGuests(searchValue);
  };

  const filterGuests = (searchValue: string) => {
    if (!searchValue.trim()) {
      const allGuests = guests.filter((guest) => guest.table);
      setFilteredGuests(allGuests);
      setGuestListVisible(true);
      setNoResultsMessage("");
      return;
    }
    const filtered = guests.filter(
      (guest) =>
        guest?.guest_name?.toLowerCase().includes(searchValue.toLowerCase()) &&
        guest.table,
    );
    setFilteredGuests(filtered);
    setGuestListVisible(true);
    
    if (filtered.length === 0 && searchValue.trim() !== "") {
      setNoResultsMessage(`No guests with the name "${searchValue}" were found. Please make sure you entered it correctly.`);
      setSelectedGuestTable(null);
    } else {
      setNoResultsMessage("");
    }
  };

  const loadGuestsWithTablemates = async (guests: Guest[]) => {
    const guestsWithTablemates = await Promise.all(
      guests.map(async (guest) => {
        if (guest.table) {
          const tableId = guest.table.split("/").pop();
          if (tableId) {
            const response =
              await api.guests.getTablesTableIdGuestsTablesTableIdGuestsGet(
                tableId,
              );
            return {
              ...guest,
              tablemates: response.data.contents.filter(
                (g) => g.guest_id !== guest.guest_id,
              ),
            };
          }
        }
        return guest;
      }),
    );
    return guestsWithTablemates;
  };

  useEffect(() => {
    if (weddingTableId) {
      api.guests
        .getWeddingsWeddingTableIdGuestsWeddingTableWeddingTableIdGuestsGet(weddingTableId)
        .then(async (data) => {
          const guestsWithTablemates = await loadGuestsWithTablemates(
            data.data.contents,
          );
          setGuests(guestsWithTablemates);
        });
    }
  }, [weddingTableId]);

  const handleTableClick = async (
    tableId: string,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    const response =
      await api.guests.getTablesTableIdGuestsTablesTableIdGuestsGet(tableId);
    setSelectedTableGuests(response.data.contents);
    setSelectedTable(tableId);
    setShowTableGuestList(true);

    // 获取点击位置
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    setListPosition({
      x: rect.right + 20,
      y: rect.top,
    });
  };

  const GuestSearchResult = ({ guest }: { guest: GuestWithTablemates }) => (
    <div className="guest-item" onClick={() => handleGuestItemClick(guest)}>
      <div>{guest.guest_name}</div>
      {guest.tablemates && guest.tablemates.length > 0 && (
        <div className="tablemates">
          Guests at the same table:{" "}
          {guest.tablemates.map((g) => g.guest_name).join(", ")}
        </div>
      )}
    </div>
  );

  const TableGuestList = ({
    position,
  }: {
    position: { x: number; y: number };
  }) => {
    const table = tables.find((t) => t.id === selectedTable);

    return (
      <div
        className="table-guest-list"
        style={{
          position: "absolute",
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: "none",
        }}
      >
        <div className="table-guest-list-header">
          <h3>{table?.name} Table Guests</h3>
          <button onClick={() => setShowTableGuestList(false)}>×</button>
        </div>
        <div className="table-guest-list-content">
          {selectedTableGuests.map((guest) => (
            <div key={guest.guest_id} className="table-guest-item">
              {guest.guest_name} ({guest.guest_count}p)
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleBack = useCallback(() => {
    navigate(`/wedding_table/${weddingTableId}/table-list`);
  }, [navigate, weddingTableId]);

  const handleScaleDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startPosX = scalePosition.x;
    const startPosY = scalePosition.y;

    const handleMouseMove = (e: MouseEvent) => {
      const newX = startPosX - (startX - e.clientX);
      const newY = startPosY - (startY - e.clientY);
      setScalePosition({
        x: newX,
        y: newY,
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const PublishedImage: React.FC<{ image: ImageType }> = ({ image }) => {
    return (
      <div
        className="published-image"
        style={{
          position: 'absolute',
          left: image.x,
          top: image.y,
          width: image.width,
          height: image.height
        }}
      >
        <img
          src={image.url}
          alt="Decoration"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      </div>
    );
  };

  const CoupleNamesDisplay = () => {
    return (
      <div
        className="couple-names-display published"
        style={{
          fontFamily: coupleNamesFont,
          fontSize: `${coupleNamesFontSize}px`,
          position: 'absolute',
          left: `${coupleNamesPosition.x}px`,
          top: `${coupleNamesPosition.y}px`,
          textAlign: 'center',
          zIndex: 10,
        }}
      >
        {coupleNames}
      </div>
    );
  };

  const WeddingDateDisplay = () => {
    return (
      <div
        className="wedding-date-display published"
        style={{
          fontFamily: weddingDateFont,
          fontSize: `${weddingDateFontSize}px`,
          position: 'absolute',
          left: `${weddingDatePosition.x}px`,
          top: `${weddingDatePosition.y}px`,
          textAlign: 'center',
          zIndex: 10,
          pointerEvents: 'none'
        }}
      >
        {weddingDate}
      </div>
    );
  };

  useEffect(() => {
    if (selectedGuestTable) {
      setTimeout(() => {
        const tableElement = document.querySelector(
          `.draggable-table[data-id="${selectedGuestTable}"]`
        );
  
        if (tableElement) {
          const container = document.querySelector('.tables-container-wrapper');
  
          if (container) {
            const tableRect = tableElement.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
  
            // 获取相对于整个文档的位置
            const pageYOffset = window.pageYOffset || document.documentElement.scrollTop;
            const pageXOffset = window.pageXOffset || document.documentElement.scrollLeft;
  
            const relativeTop = tableRect.top - containerRect.top + container.scrollTop;
            const relativeLeft = tableRect.left - containerRect.left + container.scrollLeft;
  
            // **修复移动端滚动问题**
            const finalTop = relativeTop - containerRect.height / 2 + tableRect.height / 2;
            const finalLeft = relativeLeft - containerRect.width / 2 + tableRect.width / 2;
  
            // **兼容手机端**
            if ('scrollTo' in container) {
              container.scrollTo({
                top: finalTop,
                left: finalLeft,
                behavior: 'smooth', // 确保支持 smooth 滚动
              });
            } else {
              // **fallback** 兼容不支持 `scrollTo` 的移动端浏览器
              (container as HTMLElement).scrollTop = finalTop;
              (container as HTMLElement).scrollLeft = finalLeft;
            }
  
            // **兼容 iOS / 安卓滚动到正确位置**
            window.scrollTo({
              top: pageYOffset + finalTop,
              left: pageXOffset + finalLeft,
              behavior: 'smooth',
            });
          }
        }
      }, 300); // 增加延迟，确保 DOM 渲染完成
    }
  }, [selectedGuestTable]);

  const pixelsPerCm = GRID_SIZE / REAL_GRID_SIZE;
  const displayWidth = layoutWidth * pixelsPerCm;
  const displayHeight = layoutHeight * pixelsPerCm;

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--display-width",
      `${displayWidth}px`,
    );
  }, [displayWidth]);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="table-layout-page published-page">
        <div className="button-container">
          <Button onClick={handleBack} className="back-button">
            <i className="fa fa-angle-left"></i> Back
          </Button>
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search table with your name"
              value={searchTerm}
              onChange={handleSearchChange}
              onClick={() => {
                setGuestListVisible(true);
                filterGuests("");
              }}
            />
            {guestListVisible && (
              <div className="search-guest-list">
                {filteredGuests.length > 0 ? (
                  filteredGuests.map((guest) => (
                    <GuestSearchResult key={guest.guest_id} guest={guest} />
                  ))
                ) : (
                  noResultsMessage && <div className="no-results" style={{ paddingLeft: '10px', margin: '10px 0' }}>{noResultsMessage}</div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="table-layout-page">
          <div 
            className="tables-container-wrapper" 
            ref={setContainerRef}
          >
            <div className="tables-container">
              {coupleNames && <CoupleNamesDisplay />}
              {weddingDate && <WeddingDateDisplay />}
              {images.map((image) => (
                <PublishedImage key={image.id} image={image} />
              ))}
              {tables.map((table) => (
                <DraggableTableIcon
                  key={table.id}
                  table={table}
                  onDragEnd={() => {}}
                  onResize={() => {}}
                  highlight={selectedGuestTable === table.id}
                  onClick={(event) => handleTableClick(table.id, event)}
                  disableDragResize={true}
                />
              ))}
            </div>
          </div>
        </div>
        {showTableGuestList && <TableGuestList position={listPosition} />}
        <div className="click-hint">
          <i className="fa fa-hand-pointer-o"></i>
          Click on a table to view the guest list
        </div>
      </div>
    </DndProvider>
  );
}

export default WeddingTablePublishedPage;

